/**
 * Formats a numeric price as US currency.
 *
 * By default the function assumes the value is expressed in **cents** (as Sylius returns monetary amounts)
 * and converts to dollars. Pass `alreadyDollars: true` when the value is already a dollar amount.
 *
 * @param value A price in cents (default) or dollars.
 * @param alreadyDollars Set to true if `value` is already a dollar amount.
 * @param hideSymbol If true, removes the $ sign from the result.
 * @returns A string like “$12.34” or “12.34” if hideSymbol is true.
 */
export const formatCurrency = (
  value: number | null | undefined,
  alreadyDollars: boolean = false,
  hideSymbol: boolean = false
): string => {
  const dollars = alreadyDollars ? (value ?? 0) : (value ?? 0) / 100;

  const parts = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).formatToParts(dollars);

  if (hideSymbol) {
    return parts
      .filter(part => part.type !== 'currency')
      .map(part => part.value)
      .join('');
  }

  return parts.map(part => part.value).join('');
};
